import React, { useState, useEffect, useRef, useMemo } from "react";

import { ChevronLeft } from "lucide-react";
import CompactActivityRow from "../shared/Timeline/CompactActivityRow";
import { FILTER, projectDelegationLifecycle } from "../shared/Timeline/activityItemUtils";

/**
 * NebulaActivityPanel — left sidebar activity log for Nebula mode.
 *
 * During streaming:
 *   - Completed items show as compact dot + title rows (opacity dimmed).
 *   - The active (last) item shows its description card below, pulsing.
 *
 * After streaming completes:
 *   - All items become compact rows with a caret when they have expandable content.
 *   - The last item is auto-expanded.
 *   - Clicking any row toggles its card (accordion — one open at a time).
 *
 * Hidden on mobile (< md breakpoint) — falls back to the existing beaming text.
 * Renders nothing when there are no activity items.
 */
const MIN_WIDTH = 180;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 240;

const NebulaActivityPanel = ({ activityItems = [], isStreaming = false }) => {
  const filtered = useMemo(
    () => projectDelegationLifecycle((activityItems || []).filter(FILTER)),
    [activityItems]
  );
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("nebula_activity_collapsed") === "true"
  );
  const [panelWidth, setPanelWidth] = useState(
    () => {
      const saved = parseInt(localStorage.getItem("nebula_activity_width"), 10);
      return saved >= MIN_WIDTH && saved <= MAX_WIDTH ? saved : DEFAULT_WIDTH;
    }
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(null);
  const dragStartWidth = useRef(null);
  const prevStreamingRef = useRef(isStreaming);

  const onDragStart = (e) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const delta = e.clientX - dragStartX.current;
      setPanelWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta)));
    };
    const onUp = () => {
      setIsDragging(false);
      // Persist final width once drag ends (not on every mousemove — avoids thrashing storage).
      setPanelWidth((w) => {
        localStorage.setItem("nebula_activity_width", String(w));
        return w;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    // Safety: if the cursor releases over an iframe (which swallows the
    // parent's mouseup), the window loses focus when the iframe takes it —
    // use that as a fallback to end the drag and prevent a stuck splitter.
    window.addEventListener("blur", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onUp);
    };
  }, [isDragging]);

  // Single accordion rule: expandedIndex tracks the active (last) item by default,
  // both during streaming and after. The user can click any item's chevron to toggle.
  const userToggledRef = useRef(false);

  // Reset on regeneration: when streaming transitions false → true, drop user's
  // manual override so the new generation auto-tracks fresh.
  useEffect(() => {
    if (!prevStreamingRef.current && isStreaming) {
      userToggledRef.current = false;
      setExpandedIndex(null);
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Keep expanded item synced to the active (last) item, unless the user has
  // manually toggled. Applies during streaming AND post-streaming.
  const activeIndexForExpand = filtered.length > 0 ? filtered.length - 1 : null;
  useEffect(() => {
    if (!userToggledRef.current && activeIndexForExpand !== null) {
      setExpandedIndex(activeIndexForExpand);
    }
  }, [activeIndexForExpand]);

  // Reset expanded state when activity items are cleared (mid-regeneration).
  useEffect(() => {
    if (filtered.length === 0) {
      setExpandedIndex(null);
      userToggledRef.current = false;
    }
  }, [filtered.length]);

  if (filtered.length === 0) return null;

  const activeIndex = isStreaming ? filtered.length - 1 : null;
  const toggle = (index) => {
    userToggledRef.current = true;
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  // Collapsed state — thin strip with rotated label, click anywhere to expand.
  if (collapsed) {
    return (
      <div
        className="hidden md:flex flex-col items-center w-7 flex-shrink-0 bg-fill-white dark:bg-fill-page-surface-dark cursor-pointer hover:bg-fill-gray-50 dark:hover:bg-fill-hex-222222 transition-colors relative"
        onClick={() => { setCollapsed(false); localStorage.setItem("nebula_activity_collapsed", "false"); }}
        title="Show Activity"
      >
        {/* Subtle right edge */}
        <div className="absolute right-0 inset-y-0 w-px bg-fill-gray-200/40 dark:bg-fill-panel-surface/60" />
        <div className="mt-5 flex flex-col items-center">
          <span
            className="text-[9px] font-semibold uppercase tracking-widest text-ink-gray-400 dark:text-ink-gray-500 select-none"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Activity
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="hidden md:flex flex-col flex-shrink-0 bg-fill-white dark:bg-fill-page-surface-dark overflow-hidden relative"
      style={{ width: panelWidth, userSelect: isDragging ? "none" : "auto" }}
    >
      {/* Full-viewport drag overlay: while resizing, this transparent layer
          covers everything (including any iframe in the canvas area) so the
          cursor never enters a sandboxed document where mousemove/mouseup
          would be swallowed, leaving the splitter stuck following the cursor. */}
      {isDragging && (
        <div
          className="fixed inset-0 z-[9999] cursor-col-resize"
          style={{ background: "transparent" }}
        />
      )}
      {/* Drag handle — replaces static border-r. Subtle line at rest, highlighted + resize cursor on hover/drag.
          A small pill-shaped grabber sits centered on the divider as a visible affordance. */}
      <div
        onMouseDown={onDragStart}
        className={`absolute right-0 inset-y-0 w-[5px] cursor-col-resize z-10 group flex items-center justify-center ${
          isDragging ? "bg-fill-primary/10 dark:bg-fill-hex-4f7aff/10" : ""
        }`}
        title="Drag to resize"
      >
        <div
          className={`h-full w-px transition-colors duration-150 ${
            isDragging
              ? "bg-fill-primary/60 dark:bg-fill-hex-4f7aff/60"
              : "bg-fill-gray-200/50 dark:bg-fill-panel-surface/70 group-hover:bg-fill-primary/30 dark:group-hover:bg-fill-hex-4f7aff/30"
          }`}
        />
        {/* Pill-shaped grabber, centered vertically */}
        <div
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-9 rounded-full border transition-all duration-150 pointer-events-none ${
            isDragging
              ? "bg-fill-primary dark:bg-fill-hex-4f7aff border-edge-primary dark:border-edge-hex-4f7aff shadow-sm"
              : "bg-fill-gray-300 dark:bg-fill-toolbar-hover border-edge-gray-200 dark:border-edge-panel-surface group-hover:bg-fill-primary dark:group-hover:bg-fill-hex-4f7aff group-hover:border-edge-primary dark:group-hover:border-edge-hex-4f7aff"
          }`}
        />
      </div>
      {/* Activity pill — collapses panel on click */}
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <button
          onClick={() => { setCollapsed(true); localStorage.setItem("nebula_activity_collapsed", "true"); }}
          className="flex items-center gap-1 px-2 py-[3px] rounded-full bg-fill-gray-100 dark:bg-fill-sidebar-hover-dark hover:bg-fill-gray-200 dark:hover:bg-fill-panel-surface transition-colors group"
          title="Collapse Activity"
        >
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-gray-500 dark:text-ink-gray-400">
            Activity
          </span>
          <ChevronLeft size={9} strokeWidth={2.5} className="text-ink-gray-400 dark:text-ink-gray-500 group-hover:text-ink-gray-500 dark:group-hover:text-ink-gray-400" />
        </button>
      </div>

      {/* Scrollable item list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4">
        {filtered.map((item, index) => {
          const isActive = index === activeIndex;
          const isExpanded = expandedIndex === index;
          const isLast = index === filtered.length - 1;

          return (
            <CompactActivityRow
              key={index}
              item={item}
              isActive={isActive}
              isStreaming={isStreaming}
              expanded={isExpanded}
              onToggle={() => toggle(index)}
              isLast={isLast}
              showConnector={true}
            />
          );
        })}
      </div>
    </div>
  );
};

export default NebulaActivityPanel;
